import React, { useCallback, useEffect, useMemo } from 'react'
import { useLocation } from 'react-router-dom'
import useGetProfile from './useGetProfile';
import { isAllowedDomain } from 'src/utils/emailUtils';
import { replaceString } from '../api/demo';

const getProfileStorageKey = () => {
  const href = typeof window !== 'undefined' ? String(window.location.href) : '';
  if (href.indexOf('blinkit') > -1) return 'blinkitProfile';
  if (href.indexOf('instamart') > -1) return 'instamartProfile';
  if (href.indexOf('myntra') > -1) return 'myntraProfile';
  if (href.indexOf('flipkart') > -1) return 'flipkartProfile';
  if (href.indexOf('zepto') > -1) return 'zeptoProfile';
  if (href.indexOf('bigbasket') > -1) return 'bigbasketProfile';
  return 'selectedProfileId';
};

const usePylonChat = () => {
  const User = JSON.parse(localStorage.getItem('User'));
  const location = useLocation();

  const getProfile = (() => {
    const key = getProfileStorageKey();
    try {
      const raw = sessionStorage.getItem(key);
      const parsed = raw ? JSON.parse(raw) : null;
      const base = parsed ? { ...parsed } : {};
      if (User && process.env.REACT_APP_DEMO_EMAIL && process.env.REACT_APP_DEMO_EMAIL.indexOf(User?.email) > -1) {
        const replaced = JSON.parse(replaceString(JSON.stringify(base)));
        return { ...replaced };
      }
      return base;
    } catch {
      return {};
    }
  })();


  const getPlatform = useMemo(() => {
    const pathName = location?.pathname;
    if (pathName?.includes('amazon')) return "Amazon";
    if (pathName?.includes('flipkart')) return "Flipkart";
    if (pathName?.includes('myntra')) return "Myntra";
    if (pathName?.includes('bigbasket')) return "Bigbasket";
    if (pathName?.includes('zepto')) return "Zepto";
    if (pathName?.includes('instamart')) return "Instamart";
    return "Hector";
  }, [location?.pathname]);
  

  const initializePylonWidget = () => {
    // Get user info from your application
    const currentUser = {
      id: User?.email || null,
      name: User?.fullName || null,
      email: User?.email || null,
      clientId: getProfile?.accountInfo?.name || "Hector",
      platform: getPlatform,
    };

    if (!getProfile?.accountInfo?.name) {
      console.log("Pylon: clientId (ad_account) missing, not initializing widget", currentUser);
      return;
    }

    // Remove any existing Pylon script
    const existingPylonScript = document.querySelector('script[src*="widget.usepylon.com"]');
    const existingPylonContainer = document.querySelectorAll('[data-pylon-widget]')?.length > 0
    if(existingPylonScript) existingPylonScript?.remove();
    if(existingPylonContainer) document.querySelectorAll('[data-pylon-widget]')?.forEach(el => el?.remove());

    // Configure Pylon
    window.pylon = {
      chat_settings: {
        app_id: process.env.REACT_APP_PYLON_APP_ID,
        email: currentUser.email,
        name: currentUser.name,
        position: "left"
      },
    };

    // Load Pylon script
    const script = document.createElement("script");
    script.innerHTML = `
      (function(){var e=window;var t=document;var n=function(){n.e(arguments)};n.q=[];n.e=function(e){n.q.push(e)};e.Pylon=n;var r=function(){var e=t.createElement("script");e.setAttribute("type","text/javascript");e.setAttribute("async","true");e.setAttribute("src","https://widget.usepylon.com/widget/${process.env.REACT_APP_PYLON_APP_ID}");var n=t.getElementsByTagName("script")[0];n.parentNode.insertBefore(e,n)};if(t.readyState==="complete"){r()}else if(e.addEventListener){e.addEventListener("load",r,false)}})();
    `;
    document.body.appendChild(script);

    // Wait for window.Pylon to be available, then push the event
    function onPylonReady(callback, maxAttempts = 50, interval = 100) {
      let attempts = 0;
      function check() {
        if (typeof window.Pylon === "function") {
          callback();
        } else if (attempts < maxAttempts) {
          attempts++;
          setTimeout(check, interval);
        } else {
          console.warn("Pylon widget did not load in time.");
        }
      }
      check();
    }

    onPylonReady(() => {
      console.log("Pylon widget loaded", { ad_account: currentUser?.clientId, platform: currentUser?.platform });
      // window.Pylon("setTicketFormFields", { ad_account: currentUser?.clientId, platform: currentUser?.platform });
      const hiddenCustomFields = {
        ad_account: currentUser?.clientId,
        page_url: typeof window !== 'undefined' ? window?.location?.href : '',
      };
      try {
        window.Pylon("setTicketFormFields", hiddenCustomFields);
      } catch (err) {
        console.warn("Pylon setTicketFormFields failed", err);
      }
    
      // Inject style into the Pylon iframe using MutationObserver
      const injectStyle = (iframe) => {
        if (iframe && iframe.contentDocument && iframe.contentDocument.head) {
          const style = document.createElement('style');
          style.textContent = `.PylonChat-bubbleContainer { background-color: #fff !important; }`;
          iframe.contentDocument.head.appendChild(style);
        }
      };
      // Set up MutationObserver to watch for the iframe
      const observer = new MutationObserver(() => {
        const iframe = document.querySelector('iframe.PylonChat-bubble');
        if (iframe) {
          injectStyle(iframe);
        }
      });
      // observer.observe(document.body, { childList: true, subtree: true });
      // Also try to inject immediately in case iframe is already present
      const iframe = document.querySelector('iframe.PylonChat-bubble');
      if (iframe) {
        injectStyle(iframe);
      }
      // Store observer for cleanup
      window.__pylonIframeObserver = observer;
    });
    const style = document.createElement('style');
    style.textContent = `
      .PylonChat-bubbleFrameContainer {
        left: 16px !important;
        bottom: 50px !important;
        right: unset !important;
        top: unset !important;
      }
      .PylonChat-chatWindowFrameContainer {
        left: 15px !important;
        bottom: 110px !important;
        right: unset !important;
        top: unset !important;
      }
    `;
    document.head.appendChild(style);
  }

  const updatePylonUser = useCallback(() => {
    const currentUser = {
      id: User?.email || null,
      name: User?.fullName || null,
      email: User?.email || null,
      clientId: getProfile?.accountInfo?.name || "Hector",
    };
        
    if (window.pylon) {
      window.pylon.chat_settings.email = currentUser?.email || null;
      window.pylon.chat_settings.name = currentUser?.name || null;
      window.pylon.chat_settings.position = "left"; // Ensure position is updated if user changes
    }
  }, [User?.fullName, User?.email, getProfile?.accountInfo?.name]);

  // Initialize widget when component mounts
  useEffect(() => {
  
    if (getProfile?.accountInfo?.name && User?.email && process.env.REACT_APP_PYLON_APP_ID) {
      console.log('pptp',getProfile?.accountInfo?.name)
      initializePylonWidget();
      const handleClickOutside = (e) => {
        if (!window.Pylon) return;
        
        // Check if the clicked element or any of its parents is a Pylon widget element
        const isPylonElement = 
          e.target.closest('.PylonChat-bubbleFrameContainer') ||
          e.target.closest('.PylonChat-chatWindowFrameContainer') ||
          e.target.closest('[data-pylon-widget]') ||
          (e.target.tagName === 'IFRAME' && e.target.classList.contains('PylonChat-bubble'));
        
        // Only hide if clicking outside the widget
        if (!isPylonElement) {
          window.Pylon("hide");
        }
      };

      document.addEventListener('click', handleClickOutside);

      return () => {
        document.removeEventListener('click', handleClickOutside);
      };
    }

    return () => {
      const existingPylonScript = document.querySelector('script[src*="widget.usepylon.com"]');
      const existingPylonContainer = document.querySelectorAll('[data-pylon-widget]')?.length > 0
      if(existingPylonScript) existingPylonScript?.remove();
      if(existingPylonContainer) document.querySelectorAll('[data-pylon-widget]')?.forEach(el => el?.remove());
      // Clean up MutationObserver if present
      if (window.__pylonIframeObserver) {
        window.__pylonIframeObserver.disconnect();
        delete window.__pylonIframeObserver;
      }
    }
  }, [getProfile?.accountInfo?.name, User?.email, location]);

  // Update user info when profile changes
  useEffect(() => {
    if (User) {
      updatePylonUser(User);
    }
  }, [User]);

  return {
    initializePylonWidget,
    updatePylonUser,
  };
};

export default usePylonChat; 

export const removePylonWidget = () => {
  const existingPylonScript = document.querySelector('script[src*="widget.usepylon.com"]');
  const existingPylonContainer = document.querySelectorAll('[data-pylon-widget]')?.length > 0
  if(existingPylonScript) existingPylonScript?.remove();
  if(existingPylonContainer) document.querySelectorAll('[data-pylon-widget]')?.forEach(el => el?.remove());
  if (window.__pylonIframeObserver) {
    window.__pylonIframeObserver.disconnect();
    delete window.__pylonIframeObserver;
  }
  // Optionally, clean up window.pylon and window.Pylon
  delete window.pylon;
  delete window.Pylon;
}